import { Box, Flex, Heading, Modal, ModalBody, ModalContent, ModalOverlay, Text, useDisclosure } from "@chakra-ui/react"
import { useAtomValue } from "jotai"
import React, { useCallback, useEffect } from "react"
import { breadCrumbFMPSecondLevel, breadCrumbFirstLevel, headerFooterContentWidth, headerFooterMaxWidth } from "../../../common/commonConstants"
import { SubPage, SubPageContent } from "../../../components/SubPage"
import { BreadCrumb } from "../../../components/commons/breadcrumb"
import { Colors } from "../../../theme/colors"
import { FMPActionButtons } from "../common.fmp.components/FMPActionButtons"
import { editFmpIsDirtyAtom } from "../common.fmp.hooks/editFmpDirty.store"
import { EditFireManagementPlanForm } from "./EditFireManagementPlanForm.support"
// import { EditFireManagementPlanTable } from './EditFireManagementPlanTable.support'
import { useLocation } from "react-router-dom"
import { scrollTo, useIsLoggedIn } from "../../../components/commons/utils"
import { Login } from "../../login/login"
import { EditFireManagementPlanTable } from "./EditFireManagementPlanTable.support"

interface EditFireManagementPlanProps {
  fmpId?: number
}
export const EditFireManagementPlan: React.FC<EditFireManagementPlanProps> = ({ fmpId }): JSX.Element => {
  const isLoggedIn = useIsLoggedIn()
  const { isOpen: isLoginOpen, onClose: onLoginClose, onOpen: onLoginOpen } = useDisclosure()
  const location = useLocation()
  const { fmpId: fmpIdFromLocation } = (location.state as { fmpId: number }) ?? { fmpId: undefined }
  const tableRef = React.useRef<HTMLDivElement>(null)

  const isDirty = useAtomValue(editFmpIsDirtyAtom)

  useEffect(() => {
    scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (!isLoggedIn) {
      onLoginOpen()
    }
  }, [isLoggedIn, onLoginOpen])

  // Login success handler
  const handleLoginSuccess = useCallback((): void => {
    onLoginClose()
  }, [onLoginClose])

  // Login cancel handler
  const handleLoginCancel = useCallback((): void => {
    onLoginClose()
  }, [onLoginClose])

  // Browser tab close / refresh: browsers only allow a native confirmation
  // here, not custom UI, so this shows the browser's own prompt.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [isDirty])

  return (
    <>
      <SubPage>
        <BreadCrumb
          options={{
            ...breadCrumbFirstLevel,
            ...breadCrumbFMPSecondLevel,
            thirdLevel: { title: "Edit FMP" }
          }}
        />

        <SubPageContent props={{ pt: "22px" }} innerBoxProps={{ w: headerFooterContentWidth, maxW: headerFooterMaxWidth }}>
          <Flex justifyContent="space-between" alignItems="center" w="100%" mb="3">
            <Heading variant={"RHeading1"} textAlign="left" color="primary">
              Fire Management Plan
            </Heading>
            <FMPActionButtons fmpId={fmpIdFromLocation} pageType="edit" />
          </Flex>
          <Box display="grid" gridTemplateColumns="1fr auto" alignItems="center" gridGap="8px" mb="4">
            <Box height="1px" bg={Colors.Alternative["900"]} />
            <Text variant="RCaptionBold" color="primary" fontSize="16px">
              Edit Plan
            </Text>
          </Box>
          <EditFireManagementPlanForm fmpId={fmpIdFromLocation} refToPass={tableRef} />
          <EditFireManagementPlanTable fmpId={fmpIdFromLocation} refToPass={tableRef} />
        </SubPageContent>
      </SubPage>

      {/* Login Modal */}
      <Modal isOpen={isLoginOpen} onClose={onLoginClose} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalBody padding="0">
            <Login successCallBack={handleLoginSuccess} loginCancelCallBack={handleLoginCancel} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
